import { useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { postSurveyVersionDeploy } from "@rubi/api-generated/clientRs/sdk.gen";
import type { SurveyVersionModel } from "@rubi/api-generated/clientRs/types.gen";
import { toast } from "@rubi/ui";

import type { LintResult } from "@/components/online-reporting/form-management/linter";
import { formatVersionNumber } from "@/components/online-reporting/form-management/utils";
import { VersionBadge } from "@/components/online-reporting/form-management/VersionBadge";
import { calculateNextVersion } from "@/utils/versionUtils";

interface DeployModalProps {
  open: boolean;
  onClose: () => void;
  parentSurveyId: string;
  orgId: string;
  draftVersion: SurveyVersionModel | null;
  activeVersion: SurveyVersionModel | null;
  onDeploySuccess: () => void;
  lintResult?: LintResult | null;
}

export const DeployModal = ({
  open,
  onClose,
  parentSurveyId,
  orgId,
  draftVersion,
  activeVersion,
  onDeploySuccess,
  lintResult,
}: DeployModalProps) => {
  const [versionBump, setVersionBump] = useState<"major" | "minor">("minor");
  const [changeSummary, setChangeSummary] = useState("");
  const [isDeploying, setIsDeploying] = useState(false);
  const [acknowledgedErrors, setAcknowledgedErrors] = useState(false);

  const hasLintErrors = Boolean(lintResult && lintResult.errors > 0);

  const nextVersion = activeVersion
    ? calculateNextVersion(formatVersionNumber(activeVersion), versionBump)
    : "1.0";

  const handleDeploy = async () => {
    if (!draftVersion) {
      toast.error("No draft to deploy");
      return;
    }

    setIsDeploying(true);
    try {
      const response = await postSurveyVersionDeploy({
        path: { org_id: orgId, parent_survey_id: parentSurveyId },
        body: {
          version_bump: versionBump,
          change_summary: changeSummary || undefined,
        },
      });

      toast.success(
        `Successfully deployed version ${response.data?.version_number}`,
      );
      setChangeSummary("");
      onDeploySuccess();
      handleClose();
    } catch (error) {
      console.error("Error deploying version:", error);
      toast.error("Failed to deploy version");
    } finally {
      setIsDeploying(false);
    }
  };

  const handleClose = () => {
    setAcknowledgedErrors(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Deploy New Survey Version</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          {/* Current Version Info */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Current Active Version
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {activeVersion ? (
                <VersionBadge
                  version={formatVersionNumber(activeVersion)}
                  status="active"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active version - this will be the first deployment (v1.0)
                </Typography>
              )}
            </Box>
          </Box>

          {/* Version Bump Selection - only show if there's an active version */}
          {activeVersion && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Version Bump Type
              </Typography>
              <ToggleButtonGroup
                value={versionBump}
                exclusive
                onChange={(_, value) => value && setVersionBump(value)}
                fullWidth
              >
                <ToggleButton value="minor">
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      Minor
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Non-breaking changes (
                      {calculateNextVersion(
                        formatVersionNumber(activeVersion),
                        "minor",
                      )}
                      )
                    </Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value="major">
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      Major
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Breaking changes (
                      {calculateNextVersion(
                        formatVersionNumber(activeVersion),
                        "major",
                      )}
                      )
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Change Summary */}
          <TextField
            label="Change Summary (Optional)"
            multiline
            rows={3}
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
            placeholder="Describe what changed in this version..."
            fullWidth
          />

          {/* Lint Errors Warning */}
          {hasLintErrors && (
            <Alert
              severity="error"
              icon={<ErrorOutlineIcon />}
              sx={{ "& .MuiAlert-message": { width: "100%" } }}
            >
              <Typography variant="subtitle2" gutterBottom>
                This survey has {lintResult?.errors} validation error
                {lintResult?.errors !== 1 ? "s" : ""}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Deploying with errors may cause issues with data collection or
                reporting. Please review and fix the errors before deploying.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledgedErrors}
                    onChange={(e) => setAcknowledgedErrors(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    I understand the risks and want to deploy anyway
                  </Typography>
                }
              />
            </Alert>
          )}

          {/* Warning */}
          <Alert severity="warning">
            {activeVersion
              ? "Deploying will archive the current active version and promote the draft to active. This action cannot be undone. Once connected to a config, all submissions will use this version."
              : "This will create the first active version (v1.0) of this survey. Once connected to a config, all submissions will use this version."}
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isDeploying}>
          Cancel
        </Button>
        <Button
          onClick={handleDeploy}
          variant="contained"
          color={hasLintErrors ? "error" : "primary"}
          disabled={
            isDeploying ||
            !draftVersion ||
            (hasLintErrors && !acknowledgedErrors)
          }
          startIcon={isDeploying && <CircularProgress size={16} />}
        >
          {isDeploying
            ? "Deploying..."
            : hasLintErrors
              ? `Deploy Anyway as v${nextVersion}`
              : `Deploy as v${nextVersion}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
